import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormControl } from '@material-ui/core';
import { LabeledCheckbox } from './ui/labeled-checkbox';

interface EnumCheckListProps<T extends number> {
  onChange: (value: T[]) => void;
  selectedValues?: T[];
  acceptedValues: T[];
  translations: Record<T, string>;
  cssClass?: string;
  fullWidth?: boolean;
  disabled?: boolean;
}

const EnumCheckList = <T extends number>({
  onChange,
  selectedValues = [],
  acceptedValues,
  translations,
  cssClass,
  fullWidth,
  disabled
}: EnumCheckListProps<T>) => {
  const { t } = useTranslation();

  const [checkedItems, setCheckedItems] = React.useState<boolean[]>(Array(acceptedValues.length).fill(false));
  const [allSelected, setAllSelected] = React.useState<boolean>(false);

  // Execute once on component mount to initialize checkbox states
  React.useEffect(() => {
    const initialCheckedItems = acceptedValues.map((value) => selectedValues.includes(value));
    setCheckedItems(initialCheckedItems);
    setAllSelected(initialCheckedItems.every(Boolean));
  }, []); // Empty dependency array ensures this runs only once on mount

  const handleChange = React.useCallback(
    (updatedChecked: boolean[]) => {
      const selectedValues = acceptedValues.filter((_, index) => updatedChecked[index]);
      onChange(selectedValues);
    },
    [acceptedValues, onChange]
  );

  const handleCheckAll = React.useCallback(() => {
    const updatedChecked = checkedItems.map(() => !allSelected);
    setCheckedItems(updatedChecked);
    setAllSelected(!allSelected);
    handleChange(updatedChecked);
  }, [checkedItems, allSelected, handleChange]);
  const handleItemCheckboxChange = React.useCallback(
    (index: number) => (checked: boolean) => {
      const updatedChecked = [...checkedItems];
      updatedChecked[index] = checked;
      setCheckedItems(updatedChecked);
      setAllSelected(updatedChecked.every(Boolean));
      handleChange(updatedChecked);
    },
    [checkedItems, handleChange]
  );

  const checkboxTranslation = allSelected ? 'f-com-unselect-all' : 'f-com-select-all';

  return (
    <FormControl className={cssClass} disabled={disabled} fullWidth={fullWidth}>
      <LabeledCheckbox
        checked={allSelected}
        onCheckedChange={() => handleCheckAll()}
        label={t(checkboxTranslation)}
        className="mb-1"
      />
      {acceptedValues.map((value, index) => (
        <LabeledCheckbox
          key={value}
          checked={checkedItems[index]}
          onCheckedChange={handleItemCheckboxChange(index)}
          label={t(translations[value])}
          className="mb-1"
        />
      ))}
    </FormControl>
  );
};

export default EnumCheckList;
